import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const Navbar = ({ onContactClick }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'About', href: '#about' },
    { label: 'Services', href: '#services' },
    { label: 'Membership', href: '#membership' },
    { label: 'Blog', href: '/blog' },
  ];

  const scrollToSection = (href) => {
    setMenuOpen(false);
    if (href.startsWith('/')) {
      navigate(href);
      return;
    }
    // If we're not on the homepage, navigate there first with hash
    if (location.pathname !== '/') {
      navigate('/' + href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        padding: '0 40px',
        height: '72px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: scrolled ? 'rgba(0,0,0,0.95)' : 'transparent',
        backdropFilter: scrolled ? 'blur(12px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(255,255,255,0.06)' : 'none',
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
      }}
    >
      {/* Logo */}
      <a
        href="/"
        style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
        onClick={(e) => {
          e.preventDefault();
          if (location.pathname === '/') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            navigate('/');
          }
        }}
      >
        <img
          src="https://customer-assets.emergentagent.com/job_428d7d2b-13b3-4e32-b545-4fc4e68b2c99/artifacts/5tormzbm_IMG_9806.PNG"
          alt="JM Guru"
          style={{ height: '40px', width: 'auto' }}
        />
        <span style={{
          color: '#ffffff',
          fontFamily: "'Figtree', sans-serif",
          fontSize: '15px',
          fontWeight: '600',
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          marginLeft: '12px',
        }}>
          Jedrek Magdalena
        </span>
      </a>

      {/* Desktop Nav Links */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '36px' }} className="desktop-nav">
        {navLinks.map((link) => (
          <button
            key={link.label}
            onClick={() => scrollToSection(link.href)}
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,0.75)',
              fontFamily: "'Figtree', sans-serif",
              fontSize: '14px',
              fontWeight: '400',
              letterSpacing: '0.05em',
              cursor: 'pointer',
              padding: '4px 0',
              transition: 'color 0.2s ease',
            }}
            onMouseEnter={(e) => e.target.style.color = '#ffffff'}
            onMouseLeave={(e) => e.target.style.color = 'rgba(255,255,255,0.75)'}
          >
            {link.label}
          </button>
        ))}
      </div>

      {/* CTA Button */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <button
          onClick={onContactClick}
          style={{
            background: '#ffffff',
            color: '#000000',
            border: 'none',
            borderRadius: '100px',
            padding: '10px 24px',
            fontFamily: "'Figtree', sans-serif",
            fontSize: '13px',
            fontWeight: '600',
            letterSpacing: '0.05em',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'background-color 0.2s ease, transform 0.2s ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e0e0e0'; e.currentTarget.style.transform = 'scale(1.03)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#ffffff'; e.currentTarget.style.transform = 'scale(1)'; }}
        >
          Book a Strategy Call
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2.5 9.5L9.5 2.5M9.5 2.5H4M9.5 2.5V8" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="mobile-menu-btn"
          style={{
            display: 'none',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            flexDirection: 'column',
            gap: '5px',
          }}
        >
          <span style={{ display: 'block', width: '22px', height: '2px', background: '#fff', borderRadius: '2px' }} />
          <span style={{ display: 'block', width: '22px', height: '2px', background: '#fff', borderRadius: '2px' }} />
          <span style={{ display: 'block', width: menuOpen ? '12px' : '22px', height: '2px', background: '#fff', borderRadius: '2px', transition: 'width 0.2s' }} />
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div style={{
          position: 'absolute',
          top: '72px',
          left: 0,
          right: 0,
          backgroundColor: 'rgba(0,0,0,0.97)',
          padding: '20px 40px',
          borderTop: '1px solid rgba(255,255,255,0.08)',
        }}>
          {navLinks.map((link) => (
            <button
              key={link.label}
              onClick={() => scrollToSection(link.href)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                background: 'none',
                border: 'none',
                color: 'rgba(255,255,255,0.8)',
                fontFamily: "'Figtree', sans-serif",
                fontSize: '18px',
                fontWeight: '400',
                padding: '14px 0',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
                cursor: 'pointer',
              }}
            >
              {link.label}
            </button>
          ))}
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .mobile-menu-btn { display: flex !important; }
        }
      `}</style>
    </nav>
  );
};

export default Navbar;
